import { DOCUMENT } from '@angular/common';
import { DestroyRef, Injectable, computed, effect, inject, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { ContractFieldChangeResponse } from '@interfaces/contracts/responses/contract-field-change.response';
import {
  ClientContractRevisionListItemResponse,
  ClientContractRevisionResponse,
} from '@interfaces/companys/contracts/responses/client-contract-revision.response';
import {
  ClientContractFieldCatalogResponse,
  ClientContractResponse,
} from '@interfaces/companys/contracts/responses/client-contract.response';
import { ClientContractsHttpService } from '@services/http/companys/client-contracts-http.service';
import { saveBlobAsFile } from '@src/app/core/utils/zip-download.utils';
import { RichTextVariable } from '@src/app/shared/components/rich-text-editor/rich-text-editor.models';
import { Observable, catchError, finalize, forkJoin, map, merge, of, switchMap, tap } from 'rxjs';
import { ClientContractFieldsForm } from '../../utils/client-contract-fields-form';
import { isClientContractFinalized } from '../../utils/client-contract.utils';

/** Ação em andamento, para o botão certo mostrar "carregando". */
export type ClientContractBusyAction = 'save' | 'finalize' | 'reopen' | 'delete' | 'pdf';

/**
 * Estado do editor da minuta de cliente (card 3643): a minuta carregada, o catálogo do tipo, os
 * formulários (título, campos, blocos, corpo-fonte), a régua de "alterado" e as chamadas à API. O
 * componente fica com confirmações, permissões e navegação. Mesmo desenho do
 * `PartnerContractEditorState`.
 *
 * Texto travado: o centro mostra o texto **renderizado** pelo backend (`renderedBodyControl`, só
 * leitura). Só quem está na lista de editores do texto (`canEditText`) recebe o corpo-fonte
 * (`sourceBodyControl`) e pode alterá-lo; os demais mudam o contrato pelos campos e blocos, e o
 * backend renderiza de novo a cada gravação.
 *
 * Provido no componente (uma instância por tela aberta).
 */
@Injectable()
export class ClientContractEditorState {
  private readonly contractsService = inject(ClientContractsHttpService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly document = inject(DOCUMENT);

  readonly titleControl = new FormControl<string>('', { nonNullable: true });
  /** Texto renderizado gravado (prévia no centro). Nunca é editado nem enviado. */
  readonly renderedBodyControl = new FormControl<string>('', { nonNullable: true });
  /** Corpo-fonte com os tokens; só existe (e só é enviado) para editor do texto. */
  readonly sourceBodyControl = new FormControl<string>('', { nonNullable: true });
  readonly fieldsForm = new ClientContractFieldsForm();

  readonly contract = signal<ClientContractResponse | null>(null);
  readonly catalog = signal<ClientContractFieldCatalogResponse>({ fields: [], blocks: [] });
  readonly variables = signal<RichTextVariable[]>([]);
  readonly loading = signal(true);
  readonly busy = signal<ClientContractBusyAction | null>(null);
  readonly dirty = signal(false);

  readonly revisions = signal<ClientContractRevisionListItemResponse[]>([]);
  readonly fieldChanges = signal<ContractFieldChangeResponse[]>([]);
  readonly revisionsLoading = signal(false);
  private readonly revisionsRequested = signal(false);

  readonly isFinalized = computed(() => isClientContractFinalized(this.contract()?.status));
  readonly canEditText = computed(() => !!this.contract()?.canEditText);
  /** "Editar texto" ligado: o centro troca a prévia pelo editor do corpo-fonte. */
  readonly textMode = signal(false);
  readonly unfilledKeys = computed(() => this.contract()?.unfilledVariables ?? []);

  constructor() {
    effect(() => {
      const contract = this.contract();
      if (contract && this.revisionsRequested()) untracked(() => this.loadRevisions());
    });

    merge(
      this.titleControl.valueChanges,
      this.sourceBodyControl.valueChanges,
      this.fieldsForm.root.valueChanges
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() =>
        this.dirty.set(
          this.titleControl.dirty || this.sourceBodyControl.dirty || this.fieldsForm.root.dirty
        )
      );
  }

  /** Carrega a minuta e, junto, o catálogo e as variáveis do tipo dela. */
  load(id: string): Observable<ClientContractResponse | null> {
    this.loading.set(true);
    return this.contractsService.getContract(id).pipe(
      switchMap(contract => {
        if (!contract) return of(null);
        return forkJoin({
          catalog: this.contractsService.getFieldCatalog(contract.type),
          variables: this.contractsService
            .getVariables(contract.type)
            .pipe(catchError(() => of([] as RichTextVariable[]))),
        }).pipe(
          tap(({ catalog, variables }) => {
            this.catalog.set(catalog);
            this.variables.set(variables);
            this.fieldsForm.setCatalog(catalog, contract.fields ?? {}, contract.blocks);
            this.apply(contract);
          }),
          map(() => contract)
        );
      }),
      finalize(() => this.loading.set(false))
    );
  }

  /** Título preenchido e valores dentro dos limites do catálogo. */
  isValid(): boolean {
    if (this.fieldsForm.root.disabled) return true;
    return !!this.titleControl.value.trim() && this.fieldsForm.root.valid;
  }

  save(): Observable<ClientContractResponse | null> {
    if (!this.contract()) return of(null);
    this.busy.set('save');
    return this.saveRequest().pipe(finalize(() => this.busy.set(null)));
  }

  finalize(): Observable<ClientContractResponse | null> {
    return this.runStatusChange('finalize', id => this.contractsService.finalizeContract(id));
  }

  reopen(): Observable<ClientContractResponse | null> {
    return this.runStatusChange('reopen', id => this.contractsService.reopenContract(id));
  }

  delete(): Observable<boolean> {
    const contract = this.contract();
    if (!contract) return of(false);

    this.busy.set('delete');
    return this.contractsService.deleteContract(contract.id).pipe(
      map(() => true),
      finalize(() => this.busy.set(null))
    );
  }

  /** Baixa o PDF do texto **gravado**. Quem chama decide se salva antes. */
  downloadPdf(): Observable<void> {
    const contract = this.contract();
    if (!contract) return of(undefined);

    this.busy.set('pdf');
    return this.contractsService
      .downloadContractPdf(contract.id, `${contract.contractNumber}.pdf`)
      .pipe(
        map(file => saveBlobAsFile(this.document, file.blob, file.fileName)),
        finalize(() => this.busy.set(null))
      );
  }

  /** Pede o histórico (o painel chama ao abrir). */
  requestRevisions(): void {
    if (this.revisionsRequested()) {
      this.loadRevisions();
      return;
    }
    this.revisionsRequested.set(true);
  }

  getRevision(revisionId: string): Observable<ClientContractRevisionResponse | null> {
    const contract = this.contract();
    if (!contract) return of(null);
    return this.contractsService.getContractRevision(contract.id, revisionId);
  }

  /**
   * "Usar este texto" (só editor do texto): título e corpo-fonte da revisão (com tokens e marcadores de
   * bloco) vão para o editor e a tela fica alterada; nada é gravado aqui. Sem `sourceBodyHtml` na
   * revisão não faz nada (o texto renderizado apagaria as variáveis).
   */
  useRevisionText(revision: ClientContractRevisionResponse): void {
    if (!this.canEditText() || !revision.sourceBodyHtml) return;
    this.titleControl.setValue(revision.title ?? '');
    this.sourceBodyControl.setValue(revision.sourceBodyHtml);
    this.titleControl.markAsDirty();
    this.sourceBodyControl.markAsDirty();
    this.textMode.set(true);
    this.dirty.set(true);
  }

  /** Liga/desliga "Editar texto" (só editor do texto). O que foi digitado continua até salvar. */
  toggleTextMode(): void {
    if (!this.canEditText()) return;
    this.textMode.update(on => !on);
  }

  /** Finalizar salva antes se houver mudança, para não travar um texto desatualizado. */
  private runStatusChange(
    action: 'finalize' | 'reopen',
    request: (id: string) => Observable<ClientContractResponse | null>
  ): Observable<ClientContractResponse | null> {
    const contract = this.contract();
    if (!contract) return of(null);

    this.busy.set(action);
    const saveFirst = action === 'finalize' && this.dirty() ? this.saveRequest() : of(contract);
    return saveFirst.pipe(
      switchMap(() => request(contract.id)),
      tap(updated => {
        if (updated) this.apply(updated);
      }),
      finalize(() => this.busy.set(null))
    );
  }

  /**
   * PUT da minuta, sem mexer no `busy`. Se a pessoa continuou digitando enquanto a resposta não
   * chegava, a resposta não sobrescreve os formulários (o que foi digitado sumiria em silêncio); só a
   * minuta e a prévia são atualizadas, e a tela continua alterada.
   */
  private saveRequest(): Observable<ClientContractResponse | null> {
    const contract = this.contract();
    if (!contract) return of(null);

    const sent = this.snapshot();
    // Corpo-fonte só de quem é editor do texto e só quando mudou.
    const sendSource = this.canEditText() && this.sourceBodyControl.dirty;
    return this.contractsService
      .updateContract(contract.id, {
        title: this.titleControl.value.trim(),
        fields: this.fieldsForm.readFields(),
        blocks: this.fieldsForm.readBlocks(),
        ...(sendSource ? { sourceBodyHtml: this.sourceBodyControl.value } : {}),
      })
      .pipe(
        tap(updated => {
          const saved = updated ?? contract;
          if (this.snapshot() === sent) {
            this.apply(saved, { keepSource: true });
          } else {
            this.contract.set(saved);
            this.renderedBodyControl.setValue(saved.bodyHtml ?? '', { emitEvent: false });
          }
        })
      );
  }

  private loadRevisions(): void {
    const contract = this.contract();
    if (!contract) return;

    this.revisionsLoading.set(true);
    forkJoin({
      revisions: this.contractsService.getContractRevisions(contract.id),
      fieldChanges: this.contractsService
        .getContractFieldChanges(contract.id)
        .pipe(catchError(() => of([] as ContractFieldChangeResponse[]))),
    })
      .pipe(
        finalize(() => this.revisionsLoading.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: ({ revisions, fieldChanges }) => {
          this.revisions.set([...revisions].sort((a, b) => b.revisionNumber - a.revisionNumber));
          this.fieldChanges.set(
            [...fieldChanges].sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? ''))
          );
        },
        error: () => {
          this.revisions.set([]);
          this.fieldChanges.set([]);
        },
      });
  }

  /** Foto dos campos editáveis, para saber se algo mudou durante um salvamento. */
  private snapshot(): string {
    return JSON.stringify([
      this.titleControl.value,
      this.sourceBodyControl.value,
      this.fieldsForm.root.getRawValue(),
    ]);
  }

  /**
   * Põe a minuta nos formulários sem marcar "alterado". A prévia recebe sempre o texto renderizado
   * novo; o corpo-fonte só é regravado sem `keepSource`.
   */
  private apply(contract: ClientContractResponse, options: { keepSource?: boolean } = {}): void {
    this.contract.set(contract);
    if (!contract.canEditText || isClientContractFinalized(contract.status)) {
      this.textMode.set(false);
    }

    this.titleControl.setValue(contract.title ?? '', { emitEvent: false });
    this.renderedBodyControl.setValue(contract.bodyHtml ?? '', { emitEvent: false });
    if (!options.keepSource) {
      this.sourceBodyControl.setValue(contract.sourceBodyHtml ?? '', { emitEvent: false });
    }
    this.fieldsForm.reset(contract.fields ?? {}, contract.blocks);

    this.titleControl.markAsPristine();
    this.renderedBodyControl.markAsPristine();
    this.sourceBodyControl.markAsPristine();
    this.fieldsForm.root.markAsPristine();
    this.dirty.set(false);
  }
}
